import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Lock, Mail, RefreshCw, Shield, ShieldCheck, Unlock, UserPlus, Users, Search, X } from 'lucide-react';
import { functions } from './firebase.js';
import { useAuth } from './lib/useAuth.js';

const listAppUsers = httpsCallable(functions, 'listAppUsers');
const createAppUser = httpsCallable(functions, 'createAppUser');
const setAppUserDisabled = httpsCallable(functions, 'setAppUserDisabled');
const setAppUserRoles = httpsCallable(functions, 'setAppUserRoles');

const emptyForm = {
  email: '',
  displayName: '',
  password: '',
  admin: false,
  superAdmin: false,
};

function formatDate(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function userLabel(user) {
  return user.displayName || user.email || user.uid;
}

function emailHref(target) {
  if (!target.email) return '#';
  const subject = encodeURIComponent('Bayless app access');
  const greeting = target.displayName ? `Hi ${target.displayName},` : 'Hi,';
  const body = encodeURIComponent(
    `${greeting}\n\nYour Bayless app account is ready.\n\nSign in here: https://bayless.app\n\nThanks,\nBrad`
  );
  return `mailto:${encodeURIComponent(target.email)}?subject=${subject}&body=${body}`;
}

export default function AdminUsers() {
  const { user, isSuperAdmin, refreshClaims } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const sortedUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter((item) => !needle ||
      [item.displayName, item.email, item.uid].some((value) =>
        String(value ?? '').toLowerCase().includes(needle)));
  }, [users, search]);

  async function loadUsers() {
    setLoading(true);
    setError('');
    try {
      const res = await listAppUsers();
      setUsers(res.data?.users || []);
    } catch (e) {
      setError(e.message || 'Could not load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  function replaceUser(nextUser) {
    setUsers((current) => {
      const found = current.some((item) => item.uid === nextUser.uid);
      const next = found
        ? current.map((item) => (item.uid === nextUser.uid ? nextUser : item))
        : [...current, nextUser];
      return next.sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
    });
  }

  function updateForm(field, value) {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'superAdmin' && value) next.admin = true;
      if (field === 'admin' && !value) next.superAdmin = false;
      return next;
    });
  }

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await createAppUser({
        email: form.email,
        displayName: form.displayName,
        password: form.password,
        admin: isSuperAdmin ? form.admin : false,
        superAdmin: isSuperAdmin ? form.superAdmin : false,
      });
      replaceUser(res.data.user);
      setForm(emptyForm);
      setMessage(`Created ${res.data.user.email}.`);
    } catch (e) {
      setError(e.message || 'Could not create user.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleDisabled(target) {
    setError('');
    setMessage('');
    try {
      const res = await setAppUserDisabled({
        uid: target.uid,
        disabled: !target.disabled,
      });
      replaceUser(res.data.user);
      setMessage(`${target.disabled ? 'Enabled' : 'Disabled'} ${userLabel(target)}.`);
    } catch (e) {
      setError(e.message || 'Could not update user.');
    }
  }

  async function updateRoles(target, nextRoles) {
    setError('');
    setMessage('');
    try {
      const res = await setAppUserRoles({
        uid: target.uid,
        admin: nextRoles.admin,
        superAdmin: nextRoles.superAdmin,
      });
      replaceUser(res.data.user);
      setMessage(`Updated roles for ${userLabel(target)}.`);
      if (target.uid === user.uid) await refreshClaims();
    } catch (e) {
      setError(e.message || 'Could not update roles.');
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Admin Users</h1>
          <p className="muted">Create users, disable access, and manage admin roles.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={loadUsers} disabled={loading}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <section
        className="panel"
        style={{ display: 'grid', gap: 14, marginBottom: 18 }}
      >
        <div className="section-title">
          <UserPlus size={17} />
          Add user
        </div>
        <form
          className="form"
          onSubmit={handleCreate}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <label className="form-row">
            <span>Email</span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateForm('email', e.target.value)}
              required
              placeholder="mechanic@example.com"
            />
          </label>
          <label className="form-row">
            <span>Name</span>
            <input
              value={form.displayName}
              onChange={(e) => updateForm('displayName', e.target.value)}
              placeholder="Optional"
            />
          </label>
          <label className="form-row">
            <span>Temporary password</span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateForm('password', e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
            />
          </label>

          {isSuperAdmin && (
            <div style={{ display: 'grid', gap: 8, paddingBottom: 5 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.admin}
                  onChange={(e) => updateForm('admin', e.target.checked)}
                />
                Admin
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={form.superAdmin}
                  onChange={(e) => updateForm('superAdmin', e.target.checked)}
                />
                Super admin
              </label>
            </div>
          )}

          <button type="submit" className="btn" disabled={saving}>
            <UserPlus size={16} />
            {saving ? 'Adding...' : 'Add user'}
          </button>
        </form>
      </section>

      {(error || message) && (
        <p className={error ? 'form-error' : 'muted'} style={{ marginBottom: 12 }}>
          {error || message}
        </p>
      )}

      <section className="panel" style={{ display: 'grid', gap: 12 }}>
        <div className="section-title">
          <Users size={17} />
          Users
        </div>

        <div className="admin-search">
          <Search size={16} aria-hidden="true" />
          <input aria-label="Search users" placeholder="Search name, email, or user ID"
            value={search} onChange={(e) => setSearch(e.target.value)} />
          {search && <button type="button" className="btn-link" aria-label="Clear search"
            onClick={() => setSearch('')}><X size={16} /></button>}
        </div>
        {loading ? (
          <p className="muted">Loading users...</p>
        ) : sortedUsers.length === 0 ? (
          <p className="muted">{search ? 'No matching users.' : 'No users found.'}</p>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {sortedUsers.map((item) => {
              const isSelf = item.uid === user.uid;
              return (
                <div
                  key={item.uid}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 12,
                    alignItems: 'center',
                    padding: 12,
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    background: item.disabled ? '#f8fafc' : '#fff',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong>{userLabel(item)}</strong>
                      {isSelf && <span className="badge">You</span>}
                      {item.superAdmin && (
                        <span className="badge" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <ShieldCheck size={13} />
                          Super admin
                        </span>
                      )}
                      {!item.superAdmin && item.admin && (
                        <span className="badge" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <Shield size={13} />
                          Admin
                        </span>
                      )}
                      {item.disabled && <span className="badge">Disabled</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4, overflowWrap: 'anywhere' }}>
                      {item.email || item.uid}
                    </div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      Last sign-in: {formatDate(item.lastSignInAt)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {isSuperAdmin && (
                      <>
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!item.admin}
                            disabled={item.superAdmin}
                            onChange={(e) => updateRoles(item, {
                              admin: e.target.checked,
                              superAdmin: item.superAdmin,
                            })}
                          />
                          Admin
                        </label>
                        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                          <input
                            type="checkbox"
                            checked={!!item.superAdmin}
                            onChange={(e) => updateRoles(item, {
                              admin: true,
                              superAdmin: e.target.checked,
                            })}
                          />
                          Super
                        </label>
                      </>
                    )}
                    <a
                      className="btn-secondary"
                      href={emailHref(item)}
                      aria-disabled={!item.email}
                      style={!item.email ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
                      title={item.email ? `Email ${item.email}` : 'No email available'}
                    >
                      <Mail size={15} />
                      Email
                    </a>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => toggleDisabled(item)}
                      disabled={isSelf}
                      title={isSelf ? 'You cannot disable your own account' : undefined}
                    >
                      {item.disabled ? <Unlock size={15} /> : <Lock size={15} />}
                      {item.disabled ? 'Enable' : 'Disable'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
